import React from 'react'
import { CSSTransition } from 'react-transition-group'
import cx from 'classnames'

import { Box } from 'components/layout/Box/Box'
import { ActionButton } from './common/ActionButton/ActionButton'
import { Message } from './common/Message/Message'

import { AlertHandlersContext, AlertPropsContext } from './context'
import type {
  AlertHandlersContextValue,
  AlertPropsContextValue,
} from './context'
import { SIZES, VARIANTS } from './model'

import styles from './Alert.module.scss'

type AlertProps = AlertPropsContextValue &
  AlertHandlersContextValue & {
    children?: React.ReactNode
    className?: string
    isInitialised?: boolean
  }

export const Alert = ({
  buttonClassName,
  buttonColor,
  buttonLabel,
  buttonSize,
  buttonVariant,
  children,
  className,
  message,
  size = 'medium',
  variant,
  isInitialised = false,
  onAction,
}: AlertProps) => {
  const ref = React.useRef<HTMLElement>(null)

  const propsContext = React.useMemo<AlertPropsContextValue>(
    () => ({
      buttonClassName,
      buttonColor,
      buttonLabel,
      buttonSize,
      buttonVariant,
      message,
      size,
      variant,
    }),
    [
      buttonClassName,
      buttonColor,
      buttonLabel,
      buttonSize,
      buttonVariant,
      message,
      size,
      variant,
    ],
  )

  const handlersContext = React.useMemo<AlertHandlersContextValue>(
    () => ({ onAction }),
    [onAction],
  )

  return (
    <CSSTransition
      className={cx(
        styles.alert,
        styles.variant,
        styles.size,
        {
          [styles.medium]: size === SIZES.MEDIUM,
          [styles.small]: size === SIZES.SMALL,
          [styles.error]: variant === VARIANTS.ERROR,
          [styles.info]: variant === VARIANTS.INFO,
          [styles.success]: variant === VARIANTS.SUCCESS,
          [styles.warning]: variant === VARIANTS.WARNING,
        },
        className,
      )}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        exit: styles.exit,
        exitActive: styles.exitActive,
      }}
      nodeRef={ref}
      timeout={200}
      in={isInitialised}
      mountOnEnter
      unmountOnExit
    >
      <Box
        align="center"
        ariaLabel="alert"
        ariaRole="presentation"
        justify="space-between"
        {...{ ref }}
      >
        <AlertPropsContext.Provider value={propsContext}>
          <AlertHandlersContext.Provider value={handlersContext}>
            {children}
          </AlertHandlersContext.Provider>
        </AlertPropsContext.Provider>
      </Box>
    </CSSTransition>
  )
}

Alert.ActionButton = ActionButton
Alert.Message = Message
